import { useEffect } from "react";
import axios from "axios";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const editSchema = z.object({
  name: z.string().min(2, "Name is required"),
  email: z.string().email("Enter a valid email"),
  mobile: z.string().min(7, "Enter a valid mobile"),
  password: z.string().optional(),
});
const createSchema = editSchema.extend({
  password: z.string().min(4, "Set a password (min 4 chars)"),
});

const EMPTY = { name: "", email: "", mobile: "", password: "" };

export default function FreelancerFormDialog({ open, onOpenChange, user, onSaved }) {
  const editing = !!user?.id;
  const schema = editing ? editSchema : createSchema;

  const {
    register, handleSubmit, reset, formState: { errors, isSubmitting },
  } = useForm({
    resolver: zodResolver(schema),
    defaultValues: EMPTY,
  });

  useEffect(() => {
    if (open) {
      reset(editing
        ? { name: user.name || "", email: user.email || "", mobile: user.mobile || "", password: "" }
        : EMPTY);
    }
  }, [open, editing, user, reset]);

  const onSubmit = async (values) => {
    try {
      if (editing) {
        const payload = {
          name: values.name.trim(),
          email: values.email.trim().toLowerCase(),
          mobile: values.mobile.trim(),
        };
        if (values.password) payload.password = values.password;
        await axios.put(`${API}/admin/users/${user.id}`, payload);
        toast.success("Freelancer updated");
      } else {
        await axios.post(`${API}/admin/users`, {
          name: values.name.trim(),
          email: values.email.trim().toLowerCase(),
          mobile: values.mobile.trim(),
          password: values.password,
          role: "freelancer",
        });
        toast.success("Freelancer added");
      }
      onSaved?.();
    } catch (e) {
      const msg = e?.response?.data?.detail;
      toast.error(typeof msg === "string" ? msg : "Could not save freelancer");
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="freelancer-form-dialog" className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="font-heading text-xl">{editing ? "Edit Freelancer" : "Add Freelancer"}</DialogTitle>
          <DialogDescription>{editing ? "Update the freelancer's details." : "Create a new freelancer account (mobile + email + password)."}</DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          <Field label="Full Name *" error={errors.name?.message}>
            <Input data-testid="fu-name" placeholder="e.g. Rahul Verma" {...register("name")} />
          </Field>
          <Field label="Email *" error={errors.email?.message}>
            <Input data-testid="fu-email" type="email" placeholder="name@company.com" {...register("email")} />
          </Field>
          <Field label="Mobile *" error={errors.mobile?.message}>
            <Input data-testid="fu-mobile" placeholder="10-digit mobile" {...register("mobile")} />
          </Field>
          <Field label={editing ? "New Password (leave blank to keep unchanged)" : "Password *"} error={errors.password?.message}>
            <Input data-testid="fu-password" type="password" placeholder="••••••••" {...register("password")} />
          </Field>

          <DialogFooter className="pt-2">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
            <Button type="submit" disabled={isSubmitting} data-testid="fu-submit" className="bg-primary hover:bg-primary-hover text-white">
              {isSubmitting ? <span className="inline-flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" />Saving…</span> : (editing ? "Save changes" : "Add Freelancer")}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function Field({ label, error, children }) {
  return (
    <div className="space-y-1.5">
      <Label className="text-xs font-semibold text-slate-700 uppercase tracking-wider">{label}</Label>
      {children}
      {error && <p role="alert" className="text-xs text-destructive">{error}</p>}
    </div>
  );
}
